import React, { useEffect, useMemo, useState } from 'react';
import {
    FiCheck, FiCheckCircle, FiClock, FiCopy, FiCornerUpLeft, FiEdit2, FiEye,
    FiLink, FiMoreHorizontal, FiTrash2, FiAlertCircle, FiLock, FiX
} from 'react-icons/fi';
import RichTextEditor from '../../../components/editor/RichTextEditor';
import { esHtmlVacio, normalizarHtmlMensaje } from '../../../components/editor/sanitizarHtml';
import { esImagen, esPdf, getFileName, getIconUrl, sePuedePrevisualizar } from './archivos';
import { fechaCompleta, horaRelativa } from './fechas';
import { resaltarCoincidencias } from './busqueda';
import { separarCita } from './texto';

/**
 * Un mensaje del chat del ticket.
 *
 * La burbuja ya no es clicable: las acciones (copiar, citar, enlace, editar,
 * eliminar y detalles) viven en una barra accesible que aparece al pasar por
 * encima o al enfocar con el teclado.
 */
const MensajeChat = ({
    mensaje,
    id,
    esMio,
    mostrarAvatar,
    esAnimado,
    puedeEditar,
    terminoBusqueda = '',
    esCoincidenciaActiva = false,
    destacado = false,
    enEdicion = false,
    dominioImagenes,
    onIniciarEdicion,
    onCancelarEdicion,
    onGuardarEdicion,
    onEliminar,
    onResponder,
    onIrAMensaje,
    onCopiarTexto,
    onCopiarEnlace,
    onReintentar,
    onAbrirArchivo,
}) => {
    const [detallesAbiertos, setDetallesAbiertos] = useState(false);

    // La cita de cabecera queda fuera del editor: se edita el mensaje, no aquello
    // a lo que responde
    const textoNormalizado = useMemo(() => normalizarHtmlMensaje(mensaje.texto), [mensaje.texto]);
    const { cita, cuerpo } = useMemo(() => separarCita(textoNormalizado), [textoNormalizado]);
    const [textoEditado, setTextoEditado] = useState(cuerpo);

    useEffect(() => {
        if (enEdicion) setTextoEditado(cuerpo);
    }, [enEdicion, cuerpo]);

    // Al guardar se vuelve a anteponer la cita intacta
    const guardar = () => onGuardarEdicion(`${cita}${textoEditado}`);

    const textoVisible = useMemo(
        () => (terminoBusqueda ? resaltarCoincidencias(textoNormalizado, terminoBusqueda) : textoNormalizado),
        [textoNormalizado, terminoBusqueda]
    );

    const imagenes = useMemo(
        () => (mensaje.archivos || []).filter(esImagen),
        [mensaje.archivos]
    );

    const otrosArchivos = useMemo(
        () => (mensaje.archivos || []).filter((archivo) => !esImagen(archivo)),
        [mensaje.archivos]
    );

    const avatar = mensaje.fotoPerfil
        ? (mensaje.fotoPerfil.startsWith('http') ? mensaje.fotoPerfil : `${dominioImagenes}${mensaje.fotoPerfil}`)
        : `${dominioImagenes}/images/usuario.png`;

    const clases = [
        'tk-msg',
        esMio ? 'tk-msg--own' : 'tk-msg--other',
        esAnimado ? 'tk-msg--enter' : '',
        !mostrarAvatar ? 'tk-msg--grouped' : '',
        mensaje.esNotaInterna ? 'tk-msg--nota' : '',
        mensaje.eliminado ? 'tk-msg--eliminado' : '',
        mensaje._falloEnvio ? 'tk-msg--fallido' : '',
        destacado ? 'tk-msg--destacado' : '',
        esCoincidenciaActiva ? 'tk-msg--coincidencia' : '',
    ].filter(Boolean).join(' ');

    const renderEstado = () => {
        if (mensaje._falloEnvio) {
            return (
                <button type="button" className="tk-bubble-reintentar" onClick={onReintentar}>
                    <FiAlertCircle /> No enviado · Reintentar
                </button>
            );
        }

        if (mensaje._isTemp) {
            return <span className="tk-bubble-status tk-bubble-status--pendiente" title="Enviando…"><FiClock /></span>;
        }

        if (mensaje.leido) {
            return (
                <span className="tk-bubble-status tk-bubble-status--leido" title="Leído">
                    <FiCheck /><FiCheck className="tk-bubble-check-doble" />
                </span>
            );
        }

        return <span className="tk-bubble-status" title="Entregado"><FiCheckCircle /></span>;
    };

    const renderAcciones = () => {
        if (mensaje.eliminado || enEdicion) return null;

        return (
            <div className="tk-msg-acciones" role="group" aria-label="Acciones del mensaje">
                <button type="button" title="Responder" aria-label="Responder a este mensaje" onClick={onResponder}>
                    <FiCornerUpLeft />
                </button>
                <button type="button" title="Copiar texto" aria-label="Copiar texto" onClick={onCopiarTexto}>
                    <FiCopy />
                </button>
                <button type="button" title="Copiar enlace al mensaje" aria-label="Copiar enlace al mensaje" onClick={onCopiarEnlace}>
                    <FiLink />
                </button>
                {puedeEditar && (
                    <>
                        <button type="button" title="Editar mensaje" aria-label="Editar mensaje" onClick={onIniciarEdicion}>
                            <FiEdit2 />
                        </button>
                        <button type="button" title="Eliminar mensaje" aria-label="Eliminar mensaje" onClick={onEliminar}>
                            <FiTrash2 />
                        </button>
                    </>
                )}
                <button
                    type="button"
                    title="Detalles del mensaje"
                    aria-label="Detalles del mensaje"
                    aria-expanded={detallesAbiertos}
                    className={detallesAbiertos ? 'tk-msg-accion--activa' : ''}
                    onClick={() => setDetallesAbiertos((abierto) => !abierto)}
                >
                    <FiMoreHorizontal />
                </button>
            </div>
        );
    };

    return (
        <div className={clases} id={id}>
            <div className="tk-msg-row">
                {!esMio && (mostrarAvatar
                    ? <img className="tk-msg-avatar" src={avatar} alt={`Avatar de ${mensaje.nombre || mensaje.usuario}`} />
                    : <div className="tk-msg-avatar-spacer" />)}

                <div className="tk-bubble">
                    {!esMio && (
                        <p className='tk-bubble-sender'>{mensaje.nombre} {mensaje.apellidos}</p>
                    )}

                    {mensaje.esNotaInterna && (
                        <p className="tk-bubble-nota-aviso"><FiLock /> Nota interna · no visible para el cliente</p>
                    )}

                    {mensaje.respuestaA && !mensaje.eliminado && (
                        <button
                            type="button"
                            className="tk-bubble-respuesta"
                            onClick={() => onIrAMensaje(mensaje.respuestaA.id)}
                            title="Ir al mensaje original"
                        >
                            <span className="tk-bubble-respuesta-autor">{mensaje.respuestaA.autor}</span>
                            <span className="tk-bubble-respuesta-extracto">{mensaje.respuestaA.extracto}</span>
                        </button>
                    )}

                    {mensaje.eliminado ? (
                        <p className="tk-bubble-eliminado">Mensaje eliminado</p>
                    ) : enEdicion ? (
                        <div className="tk-bubble-edicion">
                            {cita && (
                                <div
                                    className="tk-bubble-edicion-cita"
                                    dangerouslySetInnerHTML={{ __html: cita }}
                                />
                            )}
                            <RichTextEditor
                                value={textoEditado}
                                onChange={setTextoEditado}
                                onSubmit={guardar}
                                placeholder="Edita tu mensaje…"
                            />
                            <div className="tk-bubble-edicion-acciones">
                                <button
                                    type="button"
                                    className="tk-bubble-edicion-guardar"
                                    disabled={esHtmlVacio(textoEditado)}
                                    onClick={guardar}
                                >
                                    <FiCheck /> Guardar
                                </button>
                                <button type="button" className="tk-bubble-edicion-cancelar" onClick={onCancelarEdicion}>
                                    <FiX /> Cancelar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="tk-bubble-content">
                            <div
                                className='tk-bubble-text'
                                dangerouslySetInnerHTML={{ __html: textoVisible }}
                            />

                            {imagenes.length > 0 && (
                                <div className={`tk-bubble-imagenes ${imagenes.length === 1 ? 'tk-bubble-imagenes--una' : ''}`}>
                                    {imagenes.map((archivo, i) => (
                                        <button
                                            type="button"
                                            key={i}
                                            className="tk-imagen-miniatura"
                                            onClick={() => onAbrirArchivo(archivo, 'image')}
                                            title={getFileName(archivo, 30)}
                                        >
                                            <img src={archivo} alt={getFileName(archivo, 30)} loading="lazy" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {otrosArchivos.length > 0 && (
                                <div className="tk-bubble-files">
                                    {otrosArchivos.map((archivo, i) => (
                                        <button
                                            type="button"
                                            key={i}
                                            className='tk-file-pill'
                                            onClick={() => onAbrirArchivo(archivo, esPdf(archivo) ? 'pdf' : 'externo')}
                                        >
                                            <img src={getIconUrl(archivo)} alt="" />
                                            <span>{getFileName(archivo, 20)}</span>
                                            {sePuedePrevisualizar(archivo) && <FiEye className="tk-file-pill-preview" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="tk-bubble-footer">
                        {mensaje.editado && !mensaje.eliminado && <span className="tk-bubble-editado">editado</span>}
                        <span className='tk-bubble-time' title={fechaCompleta(mensaje.fecha)}>
                            {horaRelativa(mensaje.fecha)}
                        </span>
                        {esMio && !mensaje.eliminado && renderEstado()}
                    </div>

                    {detallesAbiertos && (
                        <div className="tk-bubble-expanded">
                            <div className="tk-expanded-row">
                                <span className="tk-expanded-label">Usuario:</span>
                                <span className="tk-expanded-value">@{mensaje.usuario}</span>
                            </div>
                            <div className="tk-expanded-row">
                                <span className="tk-expanded-label">Fecha completa:</span>
                                <span className="tk-expanded-value">{fechaCompleta(mensaje.fecha)}</span>
                            </div>
                            {mensaje.editado && (
                                <div className="tk-expanded-row">
                                    <span className="tk-expanded-label">Editado:</span>
                                    <span className="tk-expanded-value">{fechaCompleta(mensaje.fechaEdicion)}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {esMio && (mostrarAvatar
                    ? <img className="tk-msg-avatar" src={avatar} alt="Tu avatar" />
                    : <div className="tk-msg-avatar-spacer" />)}

                {renderAcciones()}
            </div>
        </div>
    );
};

export default MensajeChat;
